import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Snackbar,
    Alert,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// ✅ QUILL (FREE)
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { REPORT_TYPES } from './config';

interface ReportTemplateEditorProps {
    reportTypeKey: string;
}

const ReportTemplateEditor: React.FC<ReportTemplateEditorProps> = ({ reportTypeKey }) => {
    const navigate = useNavigate();
    const config = REPORT_TYPES[reportTypeKey];

    const [template, setTemplate] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [editorData, setEditorData] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [orientation, setOrientation] = useState('PORTRAIT');
    const [pageSize, setPageSize] = useState('A4');

    const [backgroundImage, setBackgroundImage] = useState('');
    const [bgFile, setBgFile] = useState<File | null>(null);

    const [notification, setNotification] = useState<{
        message: string;
        type: 'success' | 'error';
    } | null>(null);

    const [importOpen, setImportOpen] = useState(false);
    const [sessions, setSessions] = useState<any[]>([]);
    const [selectedSession, setSelectedSession] = useState('');
    const [importing, setImporting] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any>(null);
    const [viewMode, setViewMode] = useState<'VISUAL' | 'SOURCE'>('VISUAL');
    const [isLocked, setIsLocked] = useState(false);

    useEffect(() => {
        fetchTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportTypeKey]);

    const fetchTemplate = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/report-templates', {
                params: { type: config.code },
            });

            const templates = res.data || [];
            if (templates.length > 0) {
                const defaultTemplate =
                    templates.find((t: any) => t.isDefault) || templates[0];

                // Detect advanced layout
                const bodyHtml = defaultTemplate.bodyHtml || '';
                const isAdvanced = /position:\s*absolute|<div|<table/i.test(bodyHtml);
                if (isAdvanced) {
                    setViewMode('SOURCE');
                    setIsLocked(true);
                } else {
                    setViewMode('VISUAL');
                    setIsLocked(false);
                }
                setTemplate(defaultTemplate);
                setEditorData(bodyHtml);
                setTemplateName(defaultTemplate.name);
                setIsDefault(defaultTemplate.isDefault);
                setOrientation(defaultTemplate.orientation || 'PORTRAIT');
                setPageSize(defaultTemplate.pageSize || 'A4');
                setBackgroundImage(defaultTemplate.backgroundImage || '');
            } else {
                setTemplate(null);
                setEditorData(config.defaultTemplate || '');
                setTemplateName(`${config.name} Template`);
                setIsDefault(false);
                setOrientation('PORTRAIT');
                setPageSize('A4');
                setBackgroundImage('');
                setIsLocked(false);
                setViewMode('VISUAL');
            }
        } catch {
            showNotification('Failed to load template', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleResetDefault = () => {
        if (window.confirm('Are you sure you want to reset to the default system template? Unsaved changes will be lost.')) {
             setEditorData(config.defaultTemplate || '');
             showNotification('Reset to default template', 'success');
        }
    };

    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });
    };

    const handleSave = async () => {
        if (!templateName.trim()) {
            showNotification('Template name is required', 'error');
            return;
        }

        setSaving(true);
        try {
            let sessionId = template?.sessionId;

            if (!sessionId) {
                try {
                    const sessionRes = await axios.get(
                        '/system-settings/academic-sessions/active'
                    );
                    sessionId = sessionRes.data?.id;
                } catch {
                    const sessionRes = await axios.get(
                        '/system-settings/academic-sessions'
                    );
                    sessionId = sessionRes.data?.[0]?.id;
                }
            }

            if (!sessionId) {
                throw new Error('No academic session found');
            }

            let processedHtml = editorData;
            
            // Only apply space preservation in VISUAL mode to avoid breaking CSS/HTML in Source mode
            if (viewMode === 'VISUAL') {
                processedHtml = editorData.replace(/ {2,}/g, (match) => {
                    return match.replace(/ /g, '\u00a0'); // \u00a0 is &nbsp;
                });
            }

            // Upload background image if selected
            let finalBgImage = backgroundImage;
            if (bgFile) {
                const formData = new FormData();
                formData.append('file', bgFile);
                const uploadRes = await axios.post('/scholar-register/certificates/id-card/upload', formData, {
                    params: { folder: 'templates/studentIdCard' },
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                finalBgImage = uploadRes.data.url;
            }

            const payload = {
                name: templateName,
                reportType: config.code,
                bodyHtml: processedHtml,
                keywords: config.keywords.join(','),
                isDefault,
                sessionId,
                orientation,
                pageSize,
                backgroundImage: finalBgImage,
            };

            if (template?.id) {
                await axios.put(`/report-templates/${template.id}`, payload);
                showNotification('Template updated successfully', 'success');
            } else {
                const res = await axios.post('/report-templates', payload);
                setTemplate(res.data);
                showNotification('Template created successfully', 'success');
            }

            fetchTemplate();
        } catch (err: any) {
            showNotification(
                err.response?.data?.message || 'Failed to save template',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSetDefault = async () => {
        if (!template?.id) return;
        try {
            await axios.patch(`/report-templates/${template.id}/default`);
            showNotification('Set as default successfully', 'success');
            fetchTemplate();
        } catch {
            showNotification('Failed to set as default', 'error');
        }
    };

    const getPreviewHtml = () => {
        let html = editorData;
        // Basic placeholder replacement for preview
        const dummyData: Record<string, string> = {
            '#STUDENTNAME#': 'John Doe',
            '#FATHERNAME#': 'Richard Doe',
            '#MOTHERNAME#': 'Jane Doe',
            '#CLASS#': 'X',
            '#SECTION#': 'A',
            '#SCHOLARNO#': '2023001',
            '#ADMISSIONNO#': '2023001',
            '#DOB#': '01/01/2010',
            '#SESSION#': '2025-2026',
            '#DATE#': new Date().toLocaleDateString(),
            '#PHOTO#': 'https://via.placeholder.com/150',
            '#EMPLOYEENAME#': 'James Smith',
            '#EMPLOYEEID#': 'EMP001',
            '#DESIGNATION#': 'Senior Teacher',
            '#DEPARTMENT#': 'Science',
            '#VALIDUPTO#': '31/03/2026',
            '#BLOODGROUP#': 'O+',
            '#RESULT#': 'PASSED',
            '#CONDUCT#': 'GOOD',
            '#CHARACTER#': 'GOOD',
            '#REASON#': 'Parent Transfer',
            '#LASTCLASS#': 'IX',
            '#FEEAMOUNT#': '5000',
            '#INWORDS#': 'Five Thousand Only',
            '#RECEIPTNO#': 'REC001',
            '#PAYMENTMODE#': 'CASH',
            '#FEEHEAD#': 'Tuition Fee',
            '#AMOUNT#': '5000',
            '#TOTAL#': '5000',
            '#COLLECTEDBY#': 'Admin',
            '#CASTE#': 'General',
            '#CATEGORY#': 'General',
            '#ADMISSIONDATE#': '01/04/2020',
            '#REMOVALDATE#': '31/03/2025',
        };

        config.keywords.forEach((keyword) => {
            const val = dummyData[keyword] || 'SAMPLE';
            const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // For PHOTO, use img tag with preserved styles if possible, else default style
            if (keyword === '#PHOTO#') {
                // If it's inside an src attribute, just replace the URL
                if (html.match(/src=["']#PHOTO#["']/)) {
                   html = html.replace(/src=["']#PHOTO#["']/g, `src="${val}"`);
                } else {
                   // Standalone -> replace with img
                   html = html.replace(new RegExp(escaped, 'g'), `<img src="${val}" style="max-width:100px; height:auto;" />`);
                }
            } else {
                html = html.replace(new RegExp(escaped, 'g'), val);
            }
        });

        // Inject Quill CSS for preview consistency
        // If in Source mode, avoid pre-wrap to prevent layout issues with indented HTML
        const whiteSpaceStyle = viewMode === 'VISUAL' ? 'white-space: pre-wrap;' : '';

        return `
            <div class="ql-editor" style="padding: 0; ${whiteSpaceStyle}">
                ${html}
            </div>
        `;
    };

    const getPreviewContainerStyle = () => {
        if (pageSize === 'ID_CARD') {
            const isLandscape = orientation === 'LANDSCAPE';
            return {
                width: isLandscape ? '340px' : '215px',
                height: isLandscape ? '215px' : '340px',
                border: '1px solid #ccc',
                overflow: 'hidden',
                background: 'white',
                margin: '0 auto',
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative' as const,
            };
        }
        return { 
            minHeight: 300,
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative' as const,
        };
    };

    if (!config) {
        return <Typography color="error">Invalid Report Type</Typography>;
    }

    return (
        <Box>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/system-settings')}
                sx={{ mb: 2 }}
            >
                Back
            </Button>
            <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography variant="h5">{config.name} Template</Typography>
                <Box display="flex" alignItems="center">
                     <FormControlLabel
                        control={
                            <Checkbox 
                                checked={isLocked}
                                onChange={(e) => {
                                    setIsLocked(e.target.checked);
                                    if (e.target.checked) setViewMode('SOURCE');
                                }}
                            />
                        }
                        label="Lock HTML Mode"
                        sx={{ mr: 2 }}
                     />
                     <Button 
                        onClick={() => {
                            if (isLocked) {
                                showNotification('Unlock HTML Mode to switch to Visual Editor', 'error');
                                return;
                            }
                            setViewMode(viewMode === 'VISUAL' ? 'SOURCE' : 'VISUAL');
                        }} 
                        sx={{ mr: 1 }} 
                        variant="outlined" 
                        color="info"
                        disabled={isLocked}
                     >
                        {viewMode === 'VISUAL' ? 'Source Code' : 'Visual Editor'}
                     </Button>
                     <Button onClick={handleResetDefault} sx={{ mr: 1 }} color="warning">
                        Reset Default
                    </Button>
                    <Button onClick={() => setPreviewOpen(true)} sx={{ mr: 1 }} variant="outlined">
                        Preview
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                        <Paper sx={{ p: 2 }}>
                            <Box display="flex" gap={2} mb={2}>
                                <TextField
                                    fullWidth
                                    label="Template Name"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                />
                                <FormControl fullWidth>
                                    <InputLabel>Page Size</InputLabel>
                                    <Select
                                        value={pageSize}
                                        label="Page Size"
                                        onChange={(e) => setPageSize(e.target.value)}
                                    >
                                        <MenuItem value="A4">A4</MenuItem>
                                        <MenuItem value="LEGAL">Legal</MenuItem>
                                        <MenuItem value="ID_CARD">ID Card (CR80)</MenuItem>
                                        <MenuItem value="CUSTOM">Custom</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>Orientation</InputLabel>
                                    <Select
                                        value={orientation}
                                        label="Orientation"
                                        onChange={(e) => setOrientation(e.target.value)}
                                    >
                                        <MenuItem value="PORTRAIT">Portrait</MenuItem>
                                        <MenuItem value="LANDSCAPE">Landscape</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Box mb={2}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    size="small"
                                >
                                    Upload Background
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setBgFile(e.target.files[0]);
                                                setBackgroundImage(URL.createObjectURL(e.target.files[0]));
                                            }
                                        }}
                                    />
                                </Button>
                                {backgroundImage && (
                                    <Button 
                                        color="error" 
                                        size="small" 
                                        onClick={() => {
                                            setBackgroundImage('');
                                            setBgFile(null);
                                        }}
                                        sx={{ ml: 1 }}
                                    >
                                        Remove Background
                                    </Button>
                                )}
                            </Box>

                            <Box mt={2} sx={{ 
                                "& .ql-editor": { 
                                    whiteSpace: "pre-wrap !important",
                                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    minHeight: '300px'
                                } 
                            }}>
                                {viewMode === 'VISUAL' ? (
                                    <ReactQuill
                                        theme="snow"
                                        value={editorData}
                                        onChange={setEditorData}
                                        style={{ height: 400 }}
                                        modules={{
                                            toolbar: [
                                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ list: 'ordered' }, { list: 'bullet' }],
                                                [{ script: 'sub' }, { script: 'super' }],
                                                [{ indent: '-1' }, { indent: '+1' }],
                                                [{ direction: 'rtl' }],
                                                [{ size: ['small', false, 'large', 'huge'] }],
                                                [{ color: [] }, { background: [] }],
                                                [{ font: [] }],
                                                [{ align: [] }],
                                                ['link', 'image'],
                                                ['clean'],
                                            ],
                                            clipboard: {
                                                matchVisual: false,
                                            },
                                        }}
                                    />
                                ) : (
                                    <TextField
                                        multiline
                                        fullWidth
                                        minRows={20}
                                        value={editorData}
                                        onChange={(e) => setEditorData(e.target.value)}
                                        sx={{ 
                                            fontFamily: 'monospace',
                                            bgcolor: '#1e1e1e',
                                            '& .MuiInputBase-input': {
                                                color: '#d4d4d4',
                                                fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6">Keywords</Typography>
                            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                {config.keywords.map((k: string) => (
                                    <Chip
                                        key={k}
                                        label={k}
                                        size="small"
                                        onClick={() => navigator.clipboard.writeText(k)}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Preview</DialogTitle>
                <DialogContent dividers>
                    <div
                        dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                        style={getPreviewContainerStyle()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!notification}
                autoHideDuration={5000}
                onClose={() => setNotification(null)}
            >
                <Alert severity={notification?.type}>{notification?.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default ReportTemplateEditor;
